// src/CameraMicTest.tsx
import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import {
  BsCameraVideo,
  BsMic,
  BsCameraVideoOff,
  BsMicMute,
} from "react-icons/bs";
import { HiOutlineSparkles, HiChevronDown } from "react-icons/hi";
import { HiOutlineVolumeUp } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { toggleAudio, toggleBackgroundEffect, toggleVideo, updateLiveStreamSettings } from "../../../../store/reducers/livestreamSettings";
import { User } from "models/User";
import AudioOutputSelector from "../audioOutputSelector";
import { RiSoundModuleLine } from "react-icons/ri";

const CameraMicTest: React.FC = () => {
  const auth = useSelector((state: RootState) => state.auth.user as User);
  const settings = useSelector((state: RootState) => state.live.settings);
  const [cameraOn, setCameraOn] = useState<boolean>(settings.video);
  const [micOn, setMicOn] = useState<boolean>(settings.audio);
  const videoRef = useRef<HTMLVideoElement>(null);
  const dispatch = useDispatch();
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioOutput, setSelectedAudioOutput] = useState<string>('');
  const [audioLevel, setAudioLevel] = useState<number>(0);
  const [isAudioMenuOpen, setIsAudioMenuOpen] = useState<boolean>(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const microphoneRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const audioMenuRef = useRef<HTMLDivElement>(null);

  const getLocalUserMediaAndAssignToRef = React.useMemo(() => (
    videoElementRef: React.RefObject<HTMLVideoElement>,
    enableVideo: boolean,
    enableAudio: boolean,
  ) => {
    navigator.mediaDevices
      .getUserMedia({
        video: enableVideo,
        audio: enableAudio,
      })
      .then(stream => {
        const videoTrack = stream.getVideoTracks()?.length > 0;
        const audioTrack = stream.getAudioTracks()?.length > 0;

        dispatch(updateLiveStreamSettings({
          ...(videoTrack && { isCameraAccessGranted: true }),
          ...(audioTrack && { isMicAccessGranted: true }),
        }));
        let video = videoElementRef.current;
        if (video) {
          video.srcObject = stream;
          video.muted = true;
          video.play().then(r => { }).catch(err => {
            if (err.name !== 'AbortError') {
              console.error('Video play error:', err);
            }
          });
          if (selectedAudioOutput) {
            // @ts-ignore
            video.setSinkId(selectedAudioOutput).catch(err => console.error('Error setting audio output:', err));
          }
        }
      })
      .catch(() => {
        console.log("Error accessing media devices.");
        const deniedSettings: any = {};
        if (enableVideo) deniedSettings.isCameraAccessGranted = false;
        if (enableAudio) deniedSettings.isMicAccessGranted = false;

        dispatch(updateLiveStreamSettings(deniedSettings));
      });
  }, [selectedAudioOutput, micOn]);

  useEffect(() => {
    // Cleanup previous stream to prevent AbortError
    if (videoRef.current && videoRef.current.srcObject) {
      const oldStream = videoRef.current.srcObject as MediaStream;
      oldStream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }

    getLocalUserMediaAndAssignToRef(videoRef, cameraOn, micOn);
  }, [cameraOn, micOn, getLocalUserMediaAndAssignToRef]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (audioMenuRef.current && !audioMenuRef.current.contains(event.target as Node)) {
        setIsAudioMenuOpen(false);
      }
    };

    if (isAudioMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAudioMenuOpen]);

  useEffect(() => {
    const fetchAudioDevices = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioOutputs = devices.filter(device => device.kind === 'audiooutput');
        setAudioOutputDevices(audioOutputs);
        if (audioOutputs.length > 0) {
          // Prioritize Bluetooth device
          const bluetoothDevice = audioOutputs.find(device =>
            device.label.toLowerCase().includes('bluetooth') ||
            device.label.toLowerCase().includes('wireless') ||
            device.label.toLowerCase().includes('headset')
          );
          const selectedDeviceId = bluetoothDevice ? bluetoothDevice.deviceId : audioOutputs[0].deviceId;
          setSelectedAudioOutput(selectedDeviceId);
          if (videoRef.current) {
            // @ts-ignore
            videoRef.current.setSinkId(selectedDeviceId).catch(err => console.error('Error setting audio output:', err));
          }
        }
      } catch (error) {
        console.error('Error fetching audio devices:', error);
      }
    };

    fetchAudioDevices();

    const handleDeviceChange = () => {
      fetchAudioDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, []);

  const handleAudioOutputChange = (deviceId: string) => {
    setSelectedAudioOutput(deviceId);
    if (videoRef.current) {
      // @ts-ignore
      videoRef.current.setSinkId(deviceId).catch(err => console.error('Error setting audio output:', err));
    }
    setIsAudioMenuOpen(false);
  };

  const handleCameraToggle = () => {
    const newCameraState = !cameraOn;
    setCameraOn(newCameraState);
    dispatch(updateLiveStreamSettings({ video: newCameraState }));
    if (!newCameraState && videoRef.current) {
      const currentStream = videoRef.current.srcObject as MediaStream;
      if (currentStream) {
        currentStream.getTracks().forEach(track => track.stop());
      }
    }
  };

  const handleMicToggle = () => {
    const newMicState = !micOn;
    setMicOn(newMicState);
    dispatch(updateLiveStreamSettings({ audio: newMicState }));
  };

  return (
    <div className="w-full">
      {/* Video / Profile */}
      <div className="h-56 mobile:h-56 tablet:h-64 laptop:h-72 desktop:h-80 largescreen:h-80 max-h-80">
        {settings.video ? (
          <video
            ref={videoRef}
            className={classNames("w-full h-full rounded-2xl object-cover", {
              // hidden: !settings.isCameraAccessGranted,
            })}
            autoPlay
            playsInline
            muted
          />
        ) : (
          <div className="border-2 border-white rounded-2xl overflow-hidden h-full w-full flex items-center justify-center">
            <img
              className="w-full h-full object-cover rounded-2xl border-2 border-white"
              src={auth?.profileImage}
              alt="Profile"
            />
          </div>
        )}

        {/* {!settings.isCameraAccessGranted && (
      <p className="text-red-600 text-xs">Camera access is not granted.</p>
    )} */}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap justify-evenly gap-4 p-4">
        {/* Camera Toggle */}
        <div>
          <button
            onClick={() => handleCameraToggle()}
            className={classNames(
              "p-3 mobile:p-3 tablet:p-4 laptop:p-4 bg-app-yellow text-black font-bold rounded-lg flex items-center justify-center text-lg mobile:text-xl tablet:text-2xl",
              {
                "bg-red-500": !cameraOn,
                // hidden: !settings.isCameraAccessGranted,
              }
            )}
          // disabled={!settings.isCameraAccessGranted}
          >
            {cameraOn ? <BsCameraVideo size={24} /> : <BsCameraVideoOff size={24} />}
          </button>
          {/* {!settings.isCameraAccessGranted && (
        <p className="text-red-600 text-xs">
          Camera access is not granted.
        </p>
      )} */}
        </div>

        {/* Background Effects */}
        {cameraOn && (
          <div>
            <button
              onClick={() => dispatch(toggleBackgroundEffect())}
              className={classNames(
                "p-3 mobile:p-3 tablet:p-4 laptop:p-4 bg-app-yellow text-black font-bold rounded-lg flex items-center justify-center text-lg mobile:text-xl tablet:text-2xl",
                {
                  "bg-red-500": !cameraOn,
                  // hidden: !settings.isCameraAccessGranted,
                }
              )}
            // disabled={!settings.isCameraAccessGranted}
            >
              <HiOutlineSparkles size={24} />
            </button>
          </div>
        )}

        {/* Mic Toggle */}
        <div>
          <button
            // onClick={handleMicToggle}
            className={classNames(
              "p-3 mobile:p-3 tablet:p-4 laptop:p-4 bg-app-yellow text-black font-bold rounded-lg flex items-center justify-center text-lg mobile:text-xl tablet:text-2xl",
              {
                "bg-red-500": !micOn,
                // hidden: !settings.isMicAccessGranted,
              }
            )}
          // disabled={!settings.isMicAccessGranted}
          >
            {micOn ? (
              <BsMic className="w-6 h-6 tablet:w-7 tablet:h-7" />
            ) : (
              <BsMicMute className="w-6 h-6 tablet:w-7 tablet:h-7" />
            )}
          </button>
          {/* {!settings.isMicAccessGranted && (
        <p className="text-red-600 text-xs">
          Microphone access is not granted.
        </p>
      )} */}
        </div>

        {/* Audio Output */}
        {micOn && (
          <div className="relative">
            <div
              ref={audioMenuRef}
              className="audio-output-selector relative inline-block"
            >
              <button
                className={classNames(
                  "p-3 mobile:p-3 tablet:p-4 laptop:p-4 bg-app-yellow text-black font-bold rounded-lg flex items-center justify-between space-x-2 text-lg mobile:text-xl tablet:text-2xl"
                  // {
                  //     "hidden": !settings.isMicAccessGranted,
                  // }
                )}
                onClick={() => setIsAudioMenuOpen(!isAudioMenuOpen)}
              // disabled={!settings.isMicAccessGranted}
              >
                <RiSoundModuleLine className="w-6 h-6 tablet:w-7 tablet:h-7" />
                {/* <HiChevronDown className="w-6 h-6 tablet:w-7 tablet:h-7" /> */}
              </button>

              {isAudioMenuOpen && (
              <AudioOutputSelector
              showTrigger={false}
              open={isAudioMenuOpen}
              onOpenChange={setIsAudioMenuOpen}
            />
              )}
            </div>
          </div>
        )}
      </div>
    </div>

    // <div>
    //   <div
    //     className="mobile:h-56 tablet:h-56 desktop:h-64 laptop:h-64 largescreen:h-64 max-h-64"
    //   >
    //     {settings.video ? (
    //       <video
    //         ref={videoRef}
    //         className={classNames("w-full h-full rounded-2xl object-cover", {
    //           // hidden: !settings.isCameraAccessGranted,
    //         })}
    //         autoPlay
    //         playsInline
    //         muted
    //       />
    //     ) : (
    //       <div className="border-2 border-white rounded-xl">
    //         <img
    //           className="w-full max-h-64 rounded-2xl border-2 border-white"
    //           src={auth?.profileImage}
    //           alt="Profile"
    //           width={100}
    //           height={100}
    //         />
    //       </div>
    //     )}

    //     {/* {!settings.isCameraAccessGranted && (
    //       <p className="text-red-600 text-xs">Camera access is not granted.</p>
    //     )} */}
    //   </div>

    //   <div className="flex justify-center p-4 space-x-16">
    //     <div>
    //       <button
    //         // onClick={() => handleCameraToggle()}
    //         className={classNames(
    //           "p-2 mobile:p-3 tablet:p-4 laptop:p-4 bg-app-yellow text-black font-bold text-lg mobile:text-xl tablet:text-2xl laptop:text-xl rounded-lg",
    //           {
    //             "bg-red-500": !cameraOn,
    //             // hidden: !settings.isCameraAccessGranted,
    //           }
    //         )}
    //       // disabled={!settings.isCameraAccessGranted}
    //       >
    //         {cameraOn ? <BsCameraVideo
    //         //  onClick={() => dispatch(toggleVideo())} 
    //          /> : <BsCameraVideoOff 
    //         //  onClick={() => dispatch(toggleVideo())} 
    //          />}
    //       </button>
    //       {/* {!settings.isCameraAccessGranted && (
    //         <p className="text-red-600 text-xs">
    //           Camera access is not granted.
    //         </p>
    //       )} */}
    //     </div>
    //     <div className="flex flex-col items-center mb-4">
    //       {cameraOn ? (
    //         <button
    //           onClick={() => dispatch(toggleBackgroundEffect())}
    //           className={classNames(
    //             "p-2 mobile:p-3 tablet:p-4 laptop:p-4 bg-app-yellow text-black font-bold text-lg mobile:text-xl tablet:text-2xl laptop:text-xl rounded-lg",
    //             {
    //               "bg-red-500": !cameraOn,
    //               // hidden: !settings.isCameraAccessGranted,
    //             }
    //           )}
    //         // disabled={!settings.isCameraAccessGranted}
    //         >
    //           <HiOutlineSparkles color="black" size={24} />
    //         </button>
    //         ):null}
    //       </div>

    //     <div className="flex flex-col items-center mb-4">
    //       <button
    //         // onClick={ handleMicToggle}
    //         className={classNames(
    //           "p-2 mobile:p-3 tablet:p-4 laptop:p-4 bg-app-yellow text-black font-bold text-lg mobile:text-xl tablet:text-2xl laptop:text-xl rounded-lg",
    //           {
    //             "bg-red-500": !micOn,
    //             // hidden: !settings.isMicAccessGranted,
    //           }
    //         )}
    //       // disabled={!settings.isMicAccessGranted}
    //       >
    //         {micOn ? (
    //           <BsMic
    //             size="1.5rem"
    //             className="mobile:w-6 mobile:h-6 tablet:w-7 tablet:h-7 laptop:w-auto laptop:h-auto"
    //           />
    //         ) : (
    //           <BsMicMute
    //             size="1.5rem"
    //             className="mobile:w-6 mobile:h-6 tablet:w-7 tablet:h-7 laptop:w-auto laptop:h-auto"
    //           />
    //         )}
    //       </button>
    //       {/* {!settings.isMicAccessGranted && (
    //         <p className="text-red-600 text-xs">
    //           Microphone access is not granted.
    //         </p>
    //       )} */}
    //     </div>

    //     {micOn && (
    //       <div className="flex flex-col items-center mb-4">
    //         <div
    //           ref={audioMenuRef}
    //           className="audio-output-selector relative inline-block"
    //         >
    //           <button
    //             className={classNames(
    //               "p-2 mobile:p-3 tablet:p-4 laptop:p-4 bg-app-yellow text-black font-bold text-lg mobile:text-xl tablet:text-2xl laptop:text-xl rounded-lg flex items-center space-x-2",
    //               // {
    //             //     "hidden": !settings.isMicAccessGranted,
    //               // }
    //             )}
    //             onClick={() => setIsAudioMenuOpen(!isAudioMenuOpen)}
    //           // disabled={!settings.isMicAccessGranted}
    //           >
    //             <HiOutlineVolumeUp
    //               style={{ fontSize: '20px' }}
    //               className="mobile:w-6 mobile:h-6 tablet:w-7 tablet:h-7 laptop:w-auto laptop:h-auto"
    //             />
    //             <HiChevronDown
    //               style={{ fontSize: '20px' }}
    //               className="mobile:w-6 mobile:h-6 tablet:w-7 tablet:h-7 laptop:w-auto laptop:h-auto"
    //             />
    //           </button>
    //           {isAudioMenuOpen && (
    //             <div
    //               style={{
    //                 position: 'absolute',
    //                 top: '0',
    //                 left: '40px',
    //                 background: '#fff',
    //                 border: '1px solid #dfe1e5',
    //                 borderRadius: '4px',
    //                 boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    //                 zIndex: 1000,
    //                 minWidth: '180px',
    //                 padding: '8px 0',
    //               }}
    //               className="mobile:min-w-[180px] tablet:min-w-[200px]"
    //             >
    //               <select
    //                 value={selectedAudioOutput}
    //                 onChange={(e) => handleAudioOutputChange(e.target.value)}
    //                 style={{
    //                   width: '100%',
    //                   padding: '8px',
    //                   border: 'none',
    //                   background: 'transparent',
    //                   fontSize: '14px',
    //                   cursor: 'pointer',
    //                   outline: 'none',
    //                 }}
    //                 className="mobile:text-base tablet:text-base"
    //               >
    //                 {audioOutputDevices.length === 0 && (
    //                   <option value="" style={{ padding: '8px' }}>
    //                     No audio devices
    //                   </option>
    //                 )}
    //                 {audioOutputDevices.map((device) => (
    //                   <option
    //                     key={device.deviceId}
    //                     value={device.deviceId}
    //                     style={{ padding: '8px' }}
    //                   >
    //                     {device.label || `Device ${device.deviceId}`}
    //                   </option>
    //                 ))}
    //               </select>
    //             </div>
    //           )}
    //         </div>
    //       </div>
    //     )}
    //   </div>
    // </div>
  );
};
export default CameraMicTest;